/**
 * EngineDetailSearchTerm Component
 *
 * Engine-specific detail view for Search Term Engine.
 * Displays storefront summary stats and a table of predicted search terms
 * with estimated volume badges and rationale.
 */

import { useState } from "react";
import { Text, Badge, Button } from "@a7y/tailwind-catalyst";
import type {
  SearchTermEngineSpecificAnalysis,
  SearchTermItem,
} from "@a7y/types";
import type { StoreSearchResultItem } from "@a7y/types";
import { CollapsibleSection } from "./CollapsibleSection";
import { DevCardBadge } from "./DevCardBadge";
import { SearchTermResultsDialog } from "./SearchTermResultsDialog";

export interface EngineDetailSearchTermProps {
  /** Search term engine specific analysis data */
  analysis: SearchTermEngineSpecificAnalysis;
  /** Country code from audit header (e.g. "us") */
  countryCode?: string;
  /** Language code from audit header (e.g. "en") */
  languageCode?: string;
  /** Callback to fetch store search results (injected by consuming app) */
  onSearch?: (params: {
    term: string;
    storefront: string;
    countryCode: string;
    languageCode?: string;
  }) => Promise<StoreSearchResultItem[]>;
  /** When true, force-expands all collapsible sections (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TH_CLASS =
  "px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider";

const VOLUME_COLOR: Record<SearchTermItem["estimated_volume"], "green" | "amber" | "zinc"> = {
  high: "green",
  medium: "amber",
  low: "zinc",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EngineDetailSearchTerm({
  analysis,
  countryCode,
  languageCode,
  onSearch,
  printMode,
  className = "",
}: EngineDetailSearchTermProps) {
  const [termsOpen, setTermsOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const { search_terms: raw_search_terms, storefront } = analysis;

  const VOLUME_ORDER: Record<SearchTermItem["estimated_volume"], number> = {
    high: 0,
    medium: 1,
    low: 2,
  };

  const search_terms = [...raw_search_terms].sort(
    (a, b) => VOLUME_ORDER[a.estimated_volume] - VOLUME_ORDER[b.estimated_volume],
  );

  return (
    <DevCardBadge name="EngineDetailSearchTerm">
      <div className={`space-y-6 ${className}`}>
        {/* Search Terms Table */}
        {search_terms.length > 0 && (
          <CollapsibleSection
            title={`Search Terms (${search_terms.length})`}
            isExpanded={termsOpen}
            onToggle={() => setTermsOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                <thead className="bg-gray-50 dark:bg-zinc-800">
                  <tr>
                    <th className={`${TH_CLASS} text-left`}>Term</th>
                    <th className={`${TH_CLASS} text-center w-32`}>Estimated Volume</th>
                    <th className={`${TH_CLASS} text-left`}>Rationale</th>
                    <th className={`${TH_CLASS} w-20`} />
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
                  {search_terms.map((item) => (
                    <tr key={item.term} className="hover:bg-gray-50 dark:hover:bg-zinc-800">
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {item.term}
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">
                        <Badge color={VOLUME_COLOR[item.estimated_volume]}>
                          {item.estimated_volume}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                        {item.rationale}
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">
                        <Button
                          className="!py-1 !px-2 !text-xs !bg-brand-green !text-white hover:!bg-brand-green/90"
                          onClick={() => setSearchTerm(item.term)}
                        >
                          Search w/Term
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* Empty state */}
        {search_terms.length === 0 && (
          <Text className="text-sm text-gray-500 dark:text-gray-400 py-2">
            No search terms were generated for this storefront.
          </Text>
        )}

        {/* Search Results Dialog */}
        <SearchTermResultsDialog
          open={searchTerm !== null}
          onClose={() => setSearchTerm(null)}
          term={searchTerm ?? ""}
          storefront={storefront}
          countryCode={countryCode ?? "us"}
          languageCode={languageCode}
          onSearch={onSearch}
        />
      </div>
    </DevCardBadge>
  );
}
